import { makeAutoObservable } from 'mobx';
import { i18n } from 'core/localization';
import React from 'react';
import { apiFetch } from 'framework/api';
import { FormBuilderStore, SessionPersistedValues } from 'controls/react/form/formBuilderNew/formBuilderStore';
import { Configuration, FormBuilderValue } from 'controls/react/form/formBuilderNew/models';
import { TestResult } from 'controls/react/form/formBuilderNew/annotationValidator';
import { ApplicationState } from 'framework/applicationState';
import { UserSettings } from 'tools';
import qualifierCache from 'areas/services/designer/qualifierCache';
import { openHelpWindow } from 'layout/helpMapping';
import { dialogAsync } from 'controls/dialog';
import { translator } from 'core/localization/localization';
import Utils from 'tools/utils';
import {
	getQualifierPropertyData,
	getQualifierTestResult,
	getService,
	loadQualifier as loadQualifierRequest,
	loadQualifierClipboard,
	loadQualifierHelp,
	loadQualifierTemplate as loadQualifierTemplateRequest,
	postValidateQualifier,
	preValidateQualifier,
	QualifierIdentity,
	QualifierRootData,
	testQualifier,
} from './api';
import { Link } from 'controls/react/link';
import { AssetTarget } from './components/multiple-qualifiers-window/types';

const qualifierCacheTyped = qualifierCache as { cache: Record<string, QualifierRootData> };

const i18n = translator({
	'Create multiple qualifiers': { en: 'Create multiple qualifiers', no: 'Opprett flere tjenestepunkt' },
});

export type WizardStep = 'step_1' | 'transition_1_2' | 'step_2';
export type ValidationType = 'test' | 'postValidate' | 'preValidate';

export interface ValidationDetailItem {
	message: string;
	success: boolean;
}

export interface ValidationState {
	status: boolean;
	message: string;
	details: ValidationDetailItem[];
	attachment: { id: string; name: string; mimeType: string };
	type: ValidationType;
	show: boolean;
}

export interface WindowSize {
	top: number;
	left: number;
	width: number;
	height: number;
}

export interface QualifierWizardOptions {
	serviceId: string;
	elementId: string;
	// matches the old QualifierWizard.open(config): id => edit; mode:'copy' => load from clipboard; neither => template picker
	qualifierId?: string;
	mode?: 'copy';
	isQualifierShared?: boolean;
	onSave: () => void;
}

function panelPosition() {
	const panel = $('.geDiagramContainer');
	if (!panel.get(0)) return null;
	const offset = panel.offset();
	return { left: offset.left, top: offset.top, width: panel.width(), height: panel.height() };
}

export function getDefaultPosition(step: WizardStep): WindowSize {
	const width = step === 'step_1' ? 400 : 885;
	const height = 700;
	let left = 0;
	let top = 0;

	const panel = panelPosition();
	if (panel) {
		left = panel.left + (panel.width - width) / 2;
		top = panel.top + (panel.height - height) / 2 - 100;
	}

	return { width, height, left, top };
}

async function showSharedQualifierDialog(): Promise<boolean> {
	const button = await dialogAsync({
		title: i18n('Info'),
		msg: i18n(
			'Servicequalifier is shared. Changes will affect all places servicequalifier is referred. Are you sure you want to continue?',
		),
		icon: 'WARNING',
		buttons: { ok: true, cancel: true },
	});
	return button === 'ok';
}

// Replaces the old formBuilder/qualifierWizard's reduxReducer.js + reduxActions.js + reduxSagas.js. One instance
// per wizard-open session (there's no RESET action anymore — closing the wizard just discards the instance).
export class QualifierWizardStore {
	step: WizardStep = 'step_1';
	showFieldValidation = false;

	serviceId: string;
	elementId: string;
	qualifierId: string = null;
	templateId: string = null;
	category: string = null;
	isCreateMode: boolean;
	timezone: string = ApplicationState.timezone;
	inProgress = false;
	ignoreResults = false;
	windowSize: WindowSize = null;

	original: QualifierRootData = null;
	assetTargets: AssetTarget[] = [];
	showMultipleQualifierWindow = false;
	finishButtonDisabled = false;

	validation: ValidationState = null;

	formStore: FormBuilderStore = null;

	// carries agentAccountId/agentId/assetAccountId/assetId across sibling qualifiers created via
	// "create multiple qualifiers" (see multipleQualifiersWindow) — ported from formBuilder's SESSION_PERSISTED.
	persisted = new SessionPersistedValues();

	private prevalidateWaiters: ((status: boolean) => void)[] = [];

	constructor(private options: QualifierWizardOptions) {
		makeAutoObservable(this);
		this.serviceId = options.serviceId;
		this.elementId = options.elementId;
		this.qualifierId = options.qualifierId ?? null;
	}

	get identity(): QualifierIdentity {
		return { serviceId: this.serviceId, elementId: this.elementId, qualifierId: this.qualifierId };
	}

	get isQualifierShared() {
		return this.options.isQualifierShared;
	}

	get hasTest() {
		return !!this.original?.configuration?.hasTest;
	}

	get hasPreValidate() {
		return !!this.original?.configuration?.hasPreValidate;
	}

	get hasPostValidate() {
		return !!this.original?.configuration?.hasPostValidate;
	}

	get isFormError() {
		return !this.formStore || !this.formStore.valid;
	}

	get isFinish() {
		return this.step !== 'step_1';
	}

	get nextTitle() {
		if (this.step === 'step_1') return i18n('Next');
		return this.isCreateMode ? i18n('Create') : i18n('Update');
	}

	get isNextButtonDisabled() {
		if (this.finishButtonDisabled) return true;
		if (this.step === 'step_1') return !this.templateId;
		return false;
	}

	get isTestButtonHidden() {
		return this.step !== 'step_2' || !this.hasTest;
	}

	get isBackButtonHidden() {
		if (this.step === 'step_1') return true;
		if (this.step === 'step_2') return !this.templateId;
		return true;
	}

	get qualifierWidth() {
		if (this.step === 'step_1') return 400;
		return this.windowSize?.width ?? 830;
	}

	get qualifierHeight() {
		return this.windowSize?.height ?? 600;
	}

	get qualifierLeft() {
		return this.windowSize ? Math.max(0, this.windowSize.left) : undefined;
	}

	get qualifierTop() {
		return this.windowSize ? Math.max(0, this.windowSize.top) : undefined;
	}

	get qualifierFieldValue() {
		return (field: string) => this.formStore?.value[field];
	}

	async initialize() {
		if (this.options.qualifierId) {
			this.qualifierId = this.options.qualifierId;
			this.isCreateMode = false;
			await this.transitionToStep2(() => this.loadQualifier());
		} else if (this.options.mode === 'copy') {
			this.isCreateMode = true;
			await this.transitionToStep2(() => this.loadQualifier());
		} else {
			this.isCreateMode = true;
			this.step = 'step_1';
		}
	}

	private async transitionToStep2(load: () => Promise<void>) {
		this.step = 'transition_1_2';
		await load();
		this.step = 'step_2';
	}

	selectCategoryItem = (id: string, category: string) => {
		this.templateId = id;
		this.category = category;
	};

	nextStep = async () => {
		if (this.step !== 'step_1') return;
		await this.transitionToStep2(() => (this.templateId ? this.loadQualifierTemplate() : this.loadQualifier()));
	};

	backStep = () => {
		this.showFieldValidation = false;
		this.step = 'step_1';
	};

	async loadService() {
		const result = await apiFetch(getService(this.serviceId));
		if (result.data?.operatingSchedule?.timeZone) {
			this.timezone = result.data.operatingSchedule.timeZone;
		}
	}

	async loadQualifierTemplate() {
		await this.loadService();
		const result = await apiFetch(loadQualifierTemplateRequest(this.serviceId, this.templateId));
		if (result.success) {
			this.setOriginal(result.data);
		} else {
			this.showQualifierError(result.message);
		}
	}

	async loadQualifier() {
		await this.loadService();

		const cached = this.qualifierId && qualifierCacheTyped.cache?.[this.qualifierId];
		if (cached) {
			this.setOriginal(cached);
			return;
		}

		if (this.qualifierId) {
			const result = await apiFetch(loadQualifierRequest(this.identity));
			if (result.success) {
				qualifierCacheTyped.cache[result.data.id] = result.data;
				this.setOriginal(result.data);
			} else {
				this.showQualifierError(result.message);
			}
			return;
		}

		const result = await apiFetch(loadQualifierClipboard(this.serviceId));
		if (result.success) {
			this.setOriginal(result.data);
		}
	}

	private setOriginal(data: QualifierRootData) {
		// HACK for influx — ported as-is from the old formBuilder's reducers/setQualifierData.js
		if (data.className?.includes('influxdb')) {
			data.independentForms = true;
		}

		this.qualifierId = data.id;
		this.original = data;
		this.createFormStore(data);
	}

	private createFormStore(data: QualifierRootData) {
		this.formStore?.destroy();

		const configuration: Configuration = {
			...(data.configuration as unknown as Configuration),
			values: { ...data.configuration.values, warning: data.configuration.warning },
			// SQ's own "Test configuration" is driven by this store's testConfiguration(), not FormBuilder's
			// built-in test button — suppress the generic one so we don't render two.
			hasTest: false,
			hasPreValidate: false,
			hasPostValidate: false,
			lazyData: data.lazyData,
			independentForms: data.independentForms,
		};

		this.formStore = new FormBuilderStore({
			configuration,
			mode: this.isCreateMode ? 'create' : 'update',
			timezone: this.timezone,
			splitByForms: true,
			getPropertyData: (propertyPath, value) => getQualifierPropertyData(this.identity, propertyPath, value),
			sessionPersistedValues: this.persisted,
			systemType: data.systemType,
			measuredTargetSlot: (
				<Link
					onClick={() => (this.showMultipleQualifierWindow = true)}
					className={'qualifier-wizard-form__create-multiple'}
				>
					{i18n('Create multiple qualifiers')}
				</Link>
			),
		});

		if (data.agentStatus === 'AGENT_DOWN') {
			this.formStore.lock();
		}
	}

	showQualifierError(message: string) {
		Utils.showInfo(i18n('Alert'), message, null, null, () => {
			const container = document.getElementById('qualifier-wizard-container');
			container?.remove();
		});
	}

	testConfiguration = async () => {
		if (this.isFormError) {
			this.showFieldValidation = true;
			return;
		}
		await this.validateQualifier('test', this.formStore.value);
	};

	// GET-based re-fetch of the last persisted test result (used by externalConfigErrorWindow, outside the
	// active edit session — no current form values to submit)
	getTest = async () => {
		this.inProgress = true;
		try {
			const result = await apiFetch(getQualifierTestResult(this.identity));
			this.inProgress = false;
			if (this.ignoreResults) {
				this.ignoreResults = false;
				return;
			}
			this.applyValidationResult('test', result);
		} catch (e) {
			this.inProgress = false;
			this.applyValidationError('test', e as Error);
		}
	};

	async validateQualifier(type: ValidationType, value: FormBuilderValue): Promise<boolean> {
		this.showFieldValidation = false;
		if (this.validation) this.validation.show = false;
		this.inProgress = true;

		try {
			const { warning, authTypes, ...otherProperties } = value as FormBuilderValue & { authTypes?: unknown };
			void warning;
			void authTypes;

			const endpoint =
				type === 'test' ? testQualifier : type === 'preValidate' ? preValidateQualifier : postValidateQualifier;
			const result = await apiFetch(endpoint(this.identity, otherProperties));

			if ((type === 'postValidate' || type === 'preValidate') && result.success) {
				if (type === 'postValidate') {
					this.options.onSave();
				} else {
					this.resolvePrevalidateWaiters(true);
				}
				return true;
			}

			this.applyValidationResult(type, result);

			if (type === 'preValidate') {
				this.resolvePrevalidateWaiters(false);
			}
			if (type === 'postValidate' || type === 'preValidate') {
				this.finishButtonDisabled = true;
			}
			return false;
		} catch (e) {
			this.applyValidationError(type, e as Error);
			if (type === 'preValidate') {
				this.resolvePrevalidateWaiters(false);
			}
			return false;
		} finally {
			this.inProgress = false;
		}
	}

	private applyValidationResult(
		type: ValidationType,
		result: { success: boolean; message?: string; data?: TestResult },
	) {
		const data = result.data ?? ({} as TestResult);
		const details: ValidationDetailItem[] = [...(data.details ?? [])];
		const errorFields: string[] = [...(data.errorFields ?? [])];

		if (details.length === 0 && result.message) {
			details.push({ message: result.message, success: false });
		}

		if (data.validations) {
			for (const [field, message] of Object.entries(data.validations)) {
				errorFields.push(field);
				details.push({ message: `Field '${field}': ${message}`, success: false });
			}
		}

		if (this.formStore) {
			this.formStore.validator.serverErrors = errorFields;
			this.formStore.validator.modelValidator.validate();
		}

		this.validation = {
			status: result.success,
			message: result.message,
			details,
			attachment: (data as unknown as { attachment: ValidationState['attachment'] }).attachment,
			type,
			show: true,
		};
		this.showFieldValidation = true;
	}

	private applyValidationError(type: ValidationType, e: Error) {
		this.validation = {
			status: false,
			message: e.message,
			details: [{ message: e.message, success: false }],
			attachment: null,
			type,
			show: true,
		};
	}

	private resolvePrevalidateWaiters(status: boolean) {
		const waiters = this.prevalidateWaiters;
		this.prevalidateWaiters = [];
		waiters.forEach((resolve) => resolve(status));
	}

	// invoked explicitly (e.g. before a "next"/save action) rather than automatically when the last form section
	// fills up, unlike the old formBuilder's checkFormState.js — see qualifierWizardStore comments in the PR/notes
	// for why the automatic trigger wasn't ported.
	preValidate = (): Promise<boolean> => {
		if (!this.hasPreValidate) return Promise.resolve(true);
		return new Promise((resolve) => {
			this.prevalidateWaiters.push(resolve);
			void this.validateQualifier('preValidate', this.formStore.value);
		});
	};

	hideValidation = () => {
		if (this.validation) this.validation.show = false;
	};

	setShowValidation = (status: boolean) => {
		if (this.validation) this.validation.show = status;
	};

	beginSaveQualifier = async () => {
		if (this.original?.shared) {
			const pass = await showSharedQualifierDialog();
			if (!pass) return;
		}

		if (this.isFormError) {
			this.showFieldValidation = true;
			return;
		}

		if (this.hasPostValidate) {
			await this.validateQualifier('postValidate', this.formStore.value);
		} else {
			this.options.onSave();
		}
	};

	openHelp = async () => {
		const className = this.step === 'step_1' ? 'ALL' : this.original?.className;
		const result = await apiFetch(loadQualifierHelp(this.serviceId, className));
		openHelpWindow(result.data);
	};

	async loadWindowSize(autoLayout: boolean) {
		let config: WindowSize;

		if (!autoLayout) {
			try {
				config = await UserSettings.get('ServiceQualifierWindowConfig', 'sqwizard');
			} catch {
				// fall through to default position below
			}
		}

		this.windowSize = config ?? getDefaultPosition(this.step);
	}

	resetWindowSize = () => {
		this.windowSize = getDefaultPosition(this.step);
	};

	setAssetTargets = (assetTargets: AssetTarget[]) => {
		this.assetTargets = assetTargets;
	};

	setShowMultipleQualifierWindow = (status: boolean) => {
		this.showMultipleQualifierWindow = status;
	};

	destroy() {
		this.formStore?.destroy();
	}
}
